/* eslint-disable react/require-default-props */
import { Box, TextField } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

interface OTPInputProps {
  length?: number;
  value?: string;
  onChange?: (val: string) => void;
  autoFocus?: boolean;
  inputType?: 'numeric' | 'text';
  disabled?: boolean;
}

export default function OTPInput({
  length = 6,
  value: controlledValue,
  onChange,
  autoFocus = false,
  inputType = 'numeric',
  disabled = false,
}: OTPInputProps): JSX.Element {
  const isControlled = typeof controlledValue === 'string';
  const [internalValue, setInternalValue] = useState<string>(
    controlledValue || ''
  );
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const value =
    isControlled && controlledValue !== undefined
      ? controlledValue
      : internalValue;
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) inputsRef.current[0].focus();
  }, [autoFocus]);

  const setValueAt = (index: number, char: string): void => {
    const normalized =
      inputType === 'numeric' ? char.replace(/[^0-9]/g, '') : char;
    const next = digits.slice();
    next[index] = normalized;
    const joined = next.join('').slice(0, length);

    if (!isControlled) setInternalValue(joined);
    onChange?.(joined);
  };

  const focusInput = (index: number): void => {
    inputsRef.current[index]?.focus();
    inputsRef.current[index]?.select();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    idx: number
  ): void => {
    const { key } = e;
    if (key === 'Backspace') {
      e.preventDefault();
      if (digits[idx]) {
        setValueAt(idx, '');
        focusInput(idx);
      } else {
        const prev = Math.max(0, idx - 1);
        setValueAt(prev, '');
        focusInput(prev);
      }
    }

    if (key === 'ArrowLeft') focusInput(Math.max(0, idx - 1));
    if (key === 'ArrowRight') focusInput(Math.min(length - 1, idx + 1));
  };

  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    idx: number
  ): void => {
    const raw = e.target.value ?? '';
    if (!raw) {
      setValueAt(idx, '');
      return;
    }
    const char =
      inputType === 'numeric' ? raw.replace(/[^0-9]/g, '') : raw.charAt(0);
    if (!char) return;

    setValueAt(idx, char);

    if (idx < length - 1) focusInput(idx + 1);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').trim();
    const normalized =
      inputType === 'numeric' ? text.replace(/[^0-9]/g, '') : text;
    if (!normalized) return;

    const toFill = normalized.slice(0, length).split('');
    const next = digits.slice();
    for (let i = 0; i < length; i += 1) next[i] = toFill[i] ?? next[i] ?? '';

    const joined = next.join('').slice(0, length);
    if (!isControlled) setInternalValue(joined);
    onChange?.(joined);

    const firstEmpty = next.findIndex((d) => !d);
    const focusIdx =
      firstEmpty === -1 ? Math.min(length - 1, toFill.length - 1) : firstEmpty;
    focusInput(focusIdx);
  };

  return (
    <Box display="flex" gap={2} sx={{ zIndex: 9999 }}>
      {Array.from({ length }, (_, i) => (
        <TextField
          key={i}
          inputRef={(el) => {
            inputsRef.current[i] = el;
          }}
          value={digits[i] ?? ''}
          onChange={(e) => handleInput(e, i)}
          onKeyDown={(e) =>
            handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, i)
          }
          onPaste={handlePaste}
          inputProps={{
            maxLength: 1,
            inputMode: 'text',
            style: {
              textAlign: 'center',
              fontSize: '1.25rem',
              width: '3rem',
              height: '3rem',
              padding: 0,
            },
          }}
          disabled={disabled}
        />
      ))}
    </Box>
  );
}
